import type { AutoLayout } from '@lighthouse/core'
import { DIRECTION } from '@lighthouse/core'
import mitt from 'mitt'

import type { CollisionArea, FlowLayoutNode, NodeIdWithScope, Rect } from '../types'
import { transformGap2px, transformPadding2px } from '../utils/helper'

export const DOM_DATA_NODE_SCOPE = 'data-layout-scope'
export const DOM_DATA_NODE_NAME = 'data-layout-node'
export const DOM_DATA_PARENT_NAME = 'data-layout-parent'
export const DOM_DATA_LEVELS_NAME = 'data-layout-level'
export const DOM_DATA_DISABLED = 'data-layout-disabled'
export const DOM_DATA_VIRTUAL = 'data-layout-virtual'
export const DOM_DATA_NO_DRAGGABLE = 'data-layout-no-draggable'
export const DOM_DATA_REAL_PARENT_ID = 'data-layout-real-parent'

export function findNodeDom(node: NodeIdWithScope, target?: null | Element | Document, filterDisabled = false) {
    const selector = `${node.scope ? `[${DOM_DATA_NODE_SCOPE}="${node.scope}"]` : ''}[${DOM_DATA_NODE_NAME}="${node.id}"]`
    if (!filterDisabled) {
        return (target || document).querySelector<HTMLElement>(selector)
    }
    const elements = (target || document).querySelectorAll<HTMLElement>(selector)
    for (const el of elements) {
        if (!el.closest(`[${DOM_DATA_DISABLED}]`)) return el
    }
    return null
}

export function findTriggerDom(dom: HTMLElement | null | undefined, root?: HTMLElement): HTMLElement | null {
    if (!dom || dom === root) {
        return null
    }

    if (dom.hasAttribute(DOM_DATA_NODE_NAME) && !dom.hasAttribute(DOM_DATA_DISABLED) && !dom.hasAttribute(DOM_DATA_VIRTUAL)) {
        return dom
    }

    if (dom.parentElement) {
        return findTriggerDom(dom.parentElement, root)
    }

    return null
}

/**
 * 根据id和tree查找node节点
 * @date 2024/1/8 - 12:07:55
 *
 * @export
 * @param {string} id
 * @returns {(tree: FlowLayoutNode[]) => FlowLayoutNode | undefined}
 */
export function findNodeById(id: string, scope?: string) {
    const recursion = (tree: FlowLayoutNode[]): undefined | FlowLayoutNode => {
        for (const node of tree) {
            if (node.id === id && node.scope === scope) {
                return node
            }

            if (node.children) {
                const res = recursion(node.children)
                if (res) {
                    return res
                }
            }
        }
    }

    return recursion
}

export function findParentNodeById({ id, scope }: NodeIdWithScope, tree: FlowLayoutNode[]): FlowLayoutNode | undefined {
    let parent: FlowLayoutNode | undefined

    const recursion = (tree: FlowLayoutNode[]) => {
        for (const node of tree) {
            if (parent) {
                return
            }

            if (node.id === id && node.scope === scope) {
                return true
            }

            if (node.children) {
                const res = recursion(node.children)
                if (res) {
                    if (!parent) {
                        if (node.type === 'panel') {
                            return true
                        }
                        parent = node
                    }

                    return true
                }
            }
        }
    }

    recursion(tree)

    return parent
}

export function getChildrenNodes(node: FlowLayoutNode, currentPanelId?: string): FlowLayoutNode[] {
    if (node.id === 'root') {
        return node.children ?? []
    }

    if (node.type === 'container') {
        const panel = node.children?.find(item => item.id === currentPanelId)
        if (panel) {
            return panel.children ?? []
        }
        return []
    }
    return node.children ?? []
}

/**
 * 查找新选中的节点的父级和子级，需要取消选中
 * @param newSelectedId
 * @param selectedIds
 * @param tree
 * @returns
 */
export function findNewSelectParentAndChildren(newSelectedNode: NodeIdWithScope, selectedIds: NodeIdWithScope[], tree: FlowLayoutNode[]) {
    let parent: undefined | NodeIdWithScope
    const leafs: NodeIdWithScope[] = []

    function check(children: FlowLayoutNode[]): boolean | void {
        for (const child of children) {
            if (parent) {
                return
            }

            if (newSelectedNode.scope === child.scope && newSelectedNode.id === child.id) {
                if (child.children) {
                    findLeafs(child.children)
                }
                return true
            }

            if (child.children) {
                const res = check(child.children)
                if (res) {
                    if (!parent && selectedIds.some(item => item.scope === child.scope && item.id === child.id)) {
                        parent = { scope: child.scope, id: child.id }
                    }
                    return res
                }
            }
        }
    }

    function findLeafs(children: FlowLayoutNode[]) {
        for (const child of children) {
            if (selectedIds.some(item => item.scope === child.scope && item.id === child.id)) {
                leafs.push({ scope: child.scope, id: child.id })
            }

            if (child.children) {
                findLeafs(child.children)
            }
        }
    }

    check(tree)

    return { parent, children: leafs }
}

type IndicatorRectParams = {
    collisionArea: CollisionArea
    layout?: AutoLayout
    parentLayout?: AutoLayout
    nodeRect: Rect
    nextNodeRect: Rect | undefined
    parentRect: Rect
    rootRect: Rect
    indicatorSize: number
}

export function getIndicatorRect({
    collisionArea,
    layout,
    nodeRect,
    nextNodeRect,
    parentRect,
    rootRect,
    parentLayout,
    indicatorSize
}: IndicatorRectParams) {
    if (collisionArea.position === 'in') {
        const isVertical = layout?.align?.direction === 'vertical'
        const [pl, pt, pr, pb] = transformPadding2px(layout?.padding, nodeRect)
        if (isVertical) {
            return {
                left: -rootRect.left + nodeRect.left + pl,
                top: -rootRect.top + nodeRect.top + pt,
                width: nodeRect.width - pl - pr,
                height: indicatorSize
            }
        }
        return {
            left: -rootRect.left + nodeRect.left + pl,
            top: -rootRect.top + nodeRect.top + pt,
            width: indicatorSize,
            height: nodeRect.height - pt - pb
        }
    }

    const parentDirection = parentLayout?.align?.direction || DIRECTION.horizontal
    const parentIsVertical = parentDirection === DIRECTION.vertical
    const parentPadding = transformPadding2px(parentLayout?.padding, parentRect)
    const [pl, pt, pr, pb] = parentPadding
    const parentGap = transformGap2px(parentLayout?.gap, parentRect, parentDirection, parentPadding)
    const finalGap = nextNodeRect ? (parentIsVertical ? nextNodeRect.top - nodeRect.bottom : nextNodeRect.left - nodeRect.right) : parentGap

    if (parentIsVertical) {
        if (collisionArea.position === 'before') {
            return {
                left: -rootRect.left + parentRect.left + Number(pl),
                top: Math.max(-rootRect.top + parentRect.top, -rootRect.top + nodeRect.top - finalGap / 2 - indicatorSize / 2),
                width: parentRect.width - Number(pl) - Number(pr),
                height: indicatorSize
            }
        } else if (collisionArea.position === 'after') {
            return {
                left: -rootRect.left + parentRect.left + Number(pl),
                top: Math.min(
                    -rootRect.top + parentRect.bottom - indicatorSize,
                    -rootRect.top + nodeRect.top + nodeRect.height + finalGap / 2 - indicatorSize / 2
                ),
                width: parentRect.width - Number(pl) - Number(pr),
                height: indicatorSize
            }
        }
    } else {
        if (collisionArea.position === 'before') {
            return {
                left: Math.max(-rootRect.left + parentRect.left, -rootRect.left + nodeRect.left - finalGap / 2 - indicatorSize / 2),
                top: -rootRect.top + parentRect.top + Number(pt),
                width: indicatorSize,
                height: parentRect.height - Number(pt) - Number(pb)
            }
        } else if (collisionArea.position === 'after') {
            return {
                left: Math.min(
                    -rootRect.left + parentRect.right - indicatorSize,
                    -rootRect.left + nodeRect.right + finalGap / 2 - indicatorSize / 2
                ),
                top: -rootRect.top + parentRect.top + Number(pt),
                width: indicatorSize,
                height: parentRect.height - Number(pt) - Number(pb)
            }
        }
    }
}

type LayoutInteractionEventBusType = {
    updateConfig: Partial<{ data: FlowLayoutNode[]; selectedIds: NodeIdWithScope[]; scale: number }>
}
export const layoutInteractionEventBus = mitt<LayoutInteractionEventBusType>()
